import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReport, getReportSections, getRiskRatings, downloadReport, downloadDataReport, deleteReport } from '../api';
import { useAuth } from '../context/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import DetailsTab from '../components/report/DetailsTab';
import NarrativesTab from '../components/report/NarrativesTab';
import ManagementTab from '../components/report/ManagementTab';
import RecommendationsTab from '../components/report/RecommendationsTab';
import ContactsTab from '../components/report/ContactsTab';
import PlantSurveyTab from '../components/report/PlantSurveyTab';
import AssetRegisterTab from '../components/report/AssetRegisterTab';
import MonitoringTab from '../components/report/MonitoringTab';
import ExportLogTab from '../components/report/ExportLogTab';

const TABS = [
  { key: 'details', label: 'Details' },
  { key: 'narratives', label: 'Executive Summary' },
  { key: 'management', label: 'Management' },
  { key: 'plant', label: 'System Assets' },
  { key: 'assets', label: 'Asset Survey' },
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'exportLog', label: 'Export Log' },
];

export default function ReportEditorPage({ readOnly = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasFeatureForOrg } = useAuth();
  const [report, setReport] = useState(null);
  const [sections, setSections] = useState([]);
  const [riskRatings, setRiskRatings] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [dirty, setDirty] = useState(false);
  const suppressDirtyRef = useRef(false);

  const loadReport = useCallback(() => {
    suppressDirtyRef.current = true;
    getReport(id).then((r) => {
      setReport(r);
      setDirty(false);
      requestAnimationFrame(() => { suppressDirtyRef.current = false; });
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    suppressDirtyRef.current = true;
    Promise.all([
      getReport(id),
      getReportSections(),
      getRiskRatings(),
    ]).then(([r, s, rr]) => {
      setReport(r);
      setSections(s);
      setRiskRatings(rr);
    }).catch(() => {}).finally(() => {
      setLoading(false);
      requestAnimationFrame(() => { suppressDirtyRef.current = false; });
    });
  }, [id]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadReport(id);
    } catch (err) {
      alert('Failed to generate Word document: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      await downloadDataReport(id);
    } catch (err) {
      alert('Failed to generate data report: ' + err.message);
    } finally {
      setExportingData(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this report? This cannot be undone.')) return;
    try {
      await deleteReport(id);
      navigate('/reports');
    } catch (err) {
      alert('Failed to delete report: ' + err.message);
    }
  };

  if (loading) return <div className="page-container"><p>Loading report...</p></div>;
  if (!report) return <div className="page-container"><p>Report not found.</p></div>;

  const isTabEmpty = (key) => {
    switch (key) {
      case 'narratives': return !(report.narratives && report.narratives.length);
      case 'management': return !(report.answers && report.answers.length) && !(report.systemRiskRatings && report.systemRiskRatings.length);
      case 'plant': return !(report.plantItems && report.plantItems.length);
      case 'assets': return !(report.assetRegister && report.assetRegister.length);
      case 'recommendations': return !(report.recommendations && report.recommendations.length);
      case 'contacts': return !(report.contacts && report.contacts.length);
      case 'monitoring': return !(report.monitoringTasks && report.monitoringTasks.length);
      default: return false;
    }
  };

  const statusBadge = (status) => {
    const colors = { DRAFT: '#6b7280', IN_PROGRESS: '#f59e0b', REVIEW: '#3b82f6', ASSESSOR_COMPLETED: '#10b981', QA_APPROVED: '#7c3aed' };
    return <span style={{ background: colors[status] || '#6b7280', color: '#fff', padding: '3px 10px', borderRadius: 4, fontSize: '0.8rem' }}>{status}</span>;
  };

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Reports', to: '/reports' },
          { label: readOnly ? 'View Report' : 'Edit Report' },
        ]} />
        <div className="report-editor-header">
          <h2>{readOnly ? 'View' : 'Report'}: {report.siteLocationName || report.siteName || `#${report.id}`}</h2>
          <div className="report-editor-header-actions">
            {(report.organisationId === 1 || report.organisationId === 2) && (
              <button
                className="btn btn-primary"
                onClick={handleExport}
                disabled={exporting}
                style={{ fontSize: '0.85rem', padding: '4px 12px' }}
              >
                {exporting ? 'Generating...' : 'Download Word'}
              </button>
            )}
            <button
              className="btn btn-outline"
              onClick={handleExportData}
              disabled={exportingData}
              style={{ fontSize: '0.85rem', padding: '4px 12px', color: '#006600', borderColor: '#006600' }}
            >
              {exportingData ? 'Generating...' : 'Data Report'}
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                if (!dirty || window.confirm('Unsaved changes detected, these won\'t be saved if you close.')) {
                  navigate('/reports');
                }
              }}
              style={{ fontSize: '0.85rem', padding: '4px 12px', color: '#333', borderColor: '#ccc' }}
            >
              Close
            </button>
            {hasFeatureForOrg(report.organisationId, 'REPORTS', 'DELETE') && (
              <button
                className="btn"
                onClick={handleDelete}
                style={{ fontSize: '0.85rem', padding: '4px 12px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4 }}
              >
                Delete Report
              </button>
            )}
            {statusBadge(report.status)}
          </div>
        </div>

        <div className="tab-bar">
          {TABS.map((t) => (
            <button key={t.key} className={`tab-btn ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
              {t.label}
              {isTabEmpty(t.key) && <span className="tab-empty-dot" />}
            </button>
          ))}
        </div>

        <div className="tab-content" onInput={() => { if (!suppressDirtyRef.current) setDirty(true); }}>
          {activeTab === 'details' && <DetailsTab report={report} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'narratives' && <NarrativesTab report={report} sections={sections} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'management' && <ManagementTab report={report} sections={sections} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'plant' && <PlantSurveyTab report={report} sections={sections} riskRatings={riskRatings} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'assets' && <AssetRegisterTab report={report} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'recommendations' && <RecommendationsTab report={report} sections={sections} riskRatings={riskRatings} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'contacts' && <ContactsTab report={report} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'monitoring' && <MonitoringTab report={report} onSave={loadReport} readOnly={readOnly} />}
          {activeTab === 'exportLog' && <ExportLogTab report={report} />}
        </div>
      </div>
    </div>
  );
}
